import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Observable, Subscription, combineLatest, forkJoin, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { NotificationEntity } from 'src/app/models/notification.model';
import { Router } from '@angular/router';
import { FeedbackService } from 'src/app/services/feedback/feedback.service';
import { AppointmentService } from 'src/app/services/appointment/appointment.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import * as UserProfileSelectors from 'src/app/store/user-profile/user-profile.selector';
import * as AppointmentSelectors from 'src/app/store/appointment/appointment.selectors';
import * as AppointmentActions from 'src/app/store/appointment/appointment.actions';
import { AppointmentEntity } from 'src/app/models/appointment.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrls: ['./notification-dropdown.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  notifications$: Observable<NotificationEntity[]>;
  readNotifications$: Observable<NotificationEntity[]>;
  upcomingAppointments$: Observable<AppointmentEntity[]>;
  filteredNotifications: NotificationEntity[] = [];
  upcomingAppointments: AppointmentEntity[] = [];
  unreadCount = 0;
  isLoading = false;
  activeTab: 'unread' | 'read' = 'unread';
  showDropdown = false;
  private subscription = new Subscription();
  private currentUserId: string | null = null;
  // Cache for appointment statuses that we've fetched
  private appointmentStatusCache: Map<string, string> = new Map();

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private feedbackService: FeedbackService,
    private appointmentService: AppointmentService,
    private toastService: ToastService,
    private store: Store<AppState>,
    private modalService: NgbModal
  ) {
    this.notifications$ = this.notificationService.notifications$;
    this.readNotifications$ = this.notificationService.readNotifications$;
    this.upcomingAppointments$ = this.store.select(
      AppointmentSelectors.selectUpcomingAppointments
    );
  }

  ngOnInit(): void {
    // Load upcoming appointments from store
    this.store.dispatch(AppointmentActions.loadUpcomingAppointments());

    // Subscribe to user profile to get current user ID
    this.subscription.add(
      this.store
        .select(UserProfileSelectors.selectCurrentUserProfile)
        .subscribe((userProfile) => {
          this.currentUserId = userProfile?.id || null;
        })
    );

    // Subscribe to upcoming appointments
    this.subscription.add(
      this.upcomingAppointments$.subscribe((appointments) => {
        this.upcomingAppointments = appointments;
        this.cdr.markForCheck();
        })
    );

    // Subscribe to notifications with filtering
    this.subscription.add(
      combineLatest([this.notifications$, this.readNotifications$]).subscribe(
        ([notifications, readNotifications]) => {
          const customerNotifications =
            this.filterCustomerNotifications(notifications);
          const customerReadNotifications =
            this.filterCustomerNotifications(readNotifications);
          this.filteredNotifications = [
            ...customerNotifications,
            ...customerReadNotifications,
          ].sort(
            (a, b) =>
              new Date(b.createdTime).getTime() -
              new Date(a.createdTime).getTime()
          );
          this.unreadCount = this.filteredNotifications.filter(
            (n) => !n.read
          ).length;
          this.cdr.markForCheck();
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    document.removeEventListener('click', this.closeDropdownOnOutsideClick);
  }
  private filterCustomerNotifications(
    notifications: NotificationEntity[]
  ): NotificationEntity[] {
    return notifications.filter(
          (n) =>
            n.type === 'APPOINTMENT_CONFIRMED' ||
            n.type === 'APPOINTMENT_CANCELED' ||
            n.type === 'APPOINTMENT_CANCELLED' ||
            n.type === 'APPOINTMENT_BOOKED' ||
            n.type === 'APPOINTMENT_CREATED' ||
            n.type === 'APPOINTMENT_REMINDER' ||
            n.type === 'REMINDER' ||
            n.type === 'FEEDBACK_REMINDER' ||
            n.type === 'APPOINTMENT_COMPLETED' ||
            n.type === 'J_POINT_EARNED_CONFIRMATION' ||
            n.type === 'J_POINT_EARNED' ||
            n.type === 'J_POINT_EARNED_FEEDBACK' ||
            n.type === 'J_POINT_EARNED_BOOKING' ||
            n.type === 'J_POINT_EARNED_POST' ||
            n.type === 'J_POINT_EARNED_CHECK_IN' ||
            n.type === 'J_POINT_EARNED_CREATE_ACCOUNT' ||
            n.type === 'GIFT_CARD_TRANSFER' ||
            n.type === 'APPOINTMENT_CHECKED_IN' ||
            n.type === 'RECALL_REMINDER'
        );
  }
  /**
   * Filter out feedback reminder notifications if customer has already provided feedback
   */
  private filterFeedbackReminders(notifications: NotificationEntity[]): void {
    const feedbackReminders = notifications.filter(
      (n) => n.type === 'FEEDBACK_REMINDER'
    );
    const otherNotifications = notifications.filter(
      (n) => n.type !== 'FEEDBACK_REMINDER'
    );

    if (feedbackReminders.length === 0) {
      this.filteredNotifications = notifications;
      this.unreadCount = notifications.filter((n) => !n.read).length;
      this.cdr.markForCheck();
      return;
    }

    // Check feedback status for each feedback reminder
    const feedbackChecks = feedbackReminders.map((notification) => {
      const appointmentId = notification.data?.appointmentId;
      if (!appointmentId) {
        return of({ notification, hasFeedback: false });
      }

      return this.checkFeedbackExists(appointmentId).pipe(
        map((hasFeedback) => ({ notification, hasFeedback }))
      );
    });

    forkJoin(feedbackChecks).subscribe({
      next: (
        results: Array<{
          notification: NotificationEntity;
          hasFeedback: boolean;
        }>
      ) => {
        // Filter out notifications where feedback already exists
        const validFeedbackReminders = results
          .filter((result) => !result.hasFeedback)
          .map((result) => result.notification);

        this.filteredNotifications = [
          ...otherNotifications,
          ...validFeedbackReminders,
        ];
        this.unreadCount = this.filteredNotifications.filter(
          (n) => !n.read
        ).length;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('[NOTIFICATION] Error checking feedback status:', error);
        // On error, show all notifications to avoid hiding valid reminders
        this.filteredNotifications = notifications;
        this.unreadCount = notifications.filter((n) => !n.read).length;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Check if feedback exists for an appointment
   */
  private checkFeedbackExists(appointmentId: string): Observable<boolean> {
    return this.feedbackService.checkFeedbackExists(appointmentId);
  }

  async onNotificationClick(notification: NotificationEntity): Promise<void> {
    // Mark as read if not already read (similar to mobile app)
    if (!notification.read && notification.type !== 'REMINDER') {
      await this.markAsRead(notification.id);
    }

    // Navigate based on notification type
    this.handleNotificationNavigation(notification);
  }

  /**
   * Mark notification as read (matching mobile app implementation)
   */
  public async markAsRead(notificationId: string): Promise<void> {
    try {
      if (!this.currentUserId) {
        console.warn('No current user ID available');
        return;
      }

      await this.notificationService.markAsRead(notificationId);
      console.log('Notification marked as read:', notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      this.toastService.error('Error', 'Failed to mark notification as read');
    }
  }

  private handleNotificationNavigation(notification: NotificationEntity): void {
    const { data, type } = notification;

    switch (type) {
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_CONFIRMED':
      case 'APPOINTMENT_CANCELED':
      case 'APPOINTMENT_CANCELLED':
      case 'APPOINTMENT_BOOKED':
      case 'APPOINTMENT_REMINDER':
      case 'REMINDER':
      case 'FEEDBACK_REMINDER':
      case 'APPOINTMENT_COMPLETED':
      case 'APPOINTMENT_CHECKED_IN':
        if (data?.appointmentId) {
          this.router.navigate(['/appointments', data.appointmentId]);
        } else {
          this.router.navigate(['/appointments']);
        }
        break;

      case 'J_POINT_EARNED_CONFIRMATION':
      case 'J_POINT_EARNED_CHECK_IN':
      case 'J_POINT_EARNED_CREATE_ACCOUNT':
      case 'J_POINT_EARNED':
      case 'J_POINT_EARNED_FEEDBACK':
      case 'J_POINT_EARNED_BOOKING':
      case 'J_POINT_EARNED_POST':
        // Navigate to account/profile page for j-Points
        this.router.navigate(['/account']);
        break;

      case 'GIFT_CARD_TRANSFER':
        // Navigate to gift cards page
        this.router.navigate(['/gift-cards']);
        break;

      case 'RECALL_REMINDER':
        if (data?.storeId) {
          this.router.navigate(['/businesses', data.storeId]);
        } else {
          this.router.navigate(['/businesses']);
        }
        break;

      case 'ORDER_STATUS_UPDATED':
        if (data?.orderId) {
          this.router.navigate(['/orders', data.orderId]);
        } else {
          this.router.navigate(['/orders']);
        }
        break;

      case 'PROMOTION_AVAILABLE':
        if (data?.storeId) {
          this.router.navigate(['/businesses', data.storeId]);
        } else {
          this.router.navigate(['/businesses']);
        }
        break;

      default:
        this.router.navigate(['/notifications']);
        break;
    }
  }

  /**
   * Mark all notifications as read (matching mobile app implementation)
   */
  public async markAllAsRead(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      if (!this.currentUserId) {
        console.warn('No current user ID available');
        this.toastService.error('Error', 'User not authenticated');
        return;
      }

      await this.notificationService.markAllAsRead();
      console.log('All notifications marked as read');
      this.toastService.success('Success', 'All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      this.toastService.error(
        'Error',
        'Failed to mark all notifications as read'
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  async refreshNotifications(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      await this.notificationService.refreshNotifications();
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  viewAllNotifications(): void {
    this.router.navigate(['/notifications']);
  }

  getNotificationIcon(type: string): string {
    if (type.includes('APPOINTMENT')) return 'fas fa-calendar-check';
    if (type.includes('REMINDER')) return 'fas fa-bell';
    if (type.includes('FEEDBACK')) return 'fas fa-star';
    if (type.includes('PAYMENT')) return 'fas fa-credit-card';
    if (type.includes('J_POINT_EARNED')) return 'fas fa-gem';
    if (type.includes('GIFT_CARD_TRANSFER')) return 'fas fa-gift';
    if (type.includes('RECALL_REMINDER')) return 'fas fa-bell';
    return 'fas fa-info-circle';
  }

  getNotificationColor(type: string): string {
    if (type.includes('APPOINTMENT')) return 'text-primary';
    if (type.includes('REMINDER')) return 'text-warning';
    if (type.includes('PAYMENT')) return 'text-warning';
    if (type.includes('GIFT_CARD_TRANSFER')) return 'text-primary';
    if (type.includes('J_POINT_EARNED')) return 'text-success';
    if (type.includes('RECALL_REMINDER')) return 'text-warning';
    return 'text-muted';
  }

  getNotificationTitle(notification: NotificationEntity): string {
    switch (notification.type) {
      case 'APPOINTMENT_CONFIRMED':
        return 'Appointment Confirmed';
      case 'APPOINTMENT_CANCELLED':
      case 'APPOINTMENT_CANCELED':
        return 'Appointment Cancelled';
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_BOOKED':
        return 'New Appointment';
      case 'APPOINTMENT_CHECKED_IN':
        return 'Appointment Checked In';
      case 'REMINDER':
      case 'APPOINTMENT_REMINDER':
        return 'Appointment Reminder';
      case 'FEEDBACK_REMINDER':
        return 'Feedback Reminder';
      case 'APPOINTMENT_COMPLETED':
        return 'Appointment Completed';
      case 'J_POINT_EARNED_CONFIRMATION':
      case 'J_POINT_EARNED':
      case 'J_POINT_EARNED_FEEDBACK':
      case 'J_POINT_EARNED_BOOKING':
      case 'J_POINT_EARNED_POST':
      case 'J_POINT_EARNED_CHECK_IN':
      case 'J_POINT_EARNED_CREATE_ACCOUNT':
        return 'j-Points Earned!';
      case 'GIFT_CARD_TRANSFER':
        return 'Gift Card Received!';
      case 'RECALL_REMINDER':
        return 'Recall Reminder';
      default:
        return 'Notification';
    }
  }

  getNotificationMessage(notification: NotificationEntity): string {
    switch (notification.type) {
      case 'APPOINTMENT_CONFIRMED':
        return `Your appointment with ${
          notification.data?.staff
            ?.map((staff: any) => staff.displayName)
            ?.join(', ') || 'staff'
        } has been confirmed`;
      case 'APPOINTMENT_CANCELLED':
      case 'APPOINTMENT_CANCELED':
        return `Appointment with ${
          notification.data?.staff
            ?.map((s: any) => s.displayName)
            ?.join(', ') || 'staff'
        } has been cancelled`;
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_BOOKED':
        return `You have a new appointment with ${
          notification.data?.staffNames || 'staff'
        }`;
      case 'J_POINT_EARNED_CONFIRMATION':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for confirming your appointment!`;
      case 'J_POINT_EARNED':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for your payment!`;
      case 'J_POINT_EARNED_FEEDBACK':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for your feedback!`;
      case 'J_POINT_EARNED_BOOKING':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for booking services!`;
      case 'J_POINT_EARNED_POST':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for posting pictures!`;
      case 'J_POINT_EARNED_CHECK_IN':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for checking in!`;
      case 'J_POINT_EARNED_CREATE_ACCOUNT':
        return `You have earned ${
          Number(notification.data?.pointsEarned).toFixed(2) || '0'
        } j-Points for creating an account!`;
      case 'APPOINTMENT_CHECKED_IN':
        return `You have checked in for your appointment at ${
          notification.data?.storeName || 'the store'
        }`;
      case 'REMINDER':
      case 'APPOINTMENT_REMINDER':
        const appointmentDate = notification.data?.date
          ? new Date(notification.data.date)
          : new Date();
        return `You have an appointment on ${appointmentDate.toLocaleDateString(
          'en-US',
          {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }
        )} at ${appointmentDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })} at ${notification.data?.storeName || 'the store'}`;
      case 'FEEDBACK_REMINDER':
        return `Don't forget to leave feedback for your appointment at ${
          notification.data?.storeName || 'the store'
        }!`;
      case 'APPOINTMENT_COMPLETED':
        return `Thank you for your appointment at ${
          notification.data?.storeName || 'the store'
        }! We hope you had a great experience.`;
      case 'GIFT_CARD_TRANSFER':
        return `You have received a gift card from ${
          notification.data?.senderInfo?.name || 'someone'
        }!`;
      case 'RECALL_REMINDER':
        return `Time for your next appointment at ${
          notification.data?.storeName || 'the store'
        }!`;
      default:
        return notification.message || 'You have a new notification';
    }
  }

  formatTime(date: Date): string {
    return this.notificationService.formatNotificationTime(date);
  }

  trackByNotificationId(
    index: number,
    notification: NotificationEntity
  ): string {
    return notification.id;
  }

  /**
   * Get unread notifications
   */
  getUnreadNotifications(): NotificationEntity[] {
    return this.filteredNotifications.filter((n) => !n.read);
  }

  /**
   * Get read notifications
   */
  getReadNotifications(): NotificationEntity[] {
    return this.filteredNotifications.filter((n) => n.read);
  }

  /**
   * Get today's notifications from a list
   * Compares dates in UTC to avoid timezone issues
   */
  getTodayNotifications(
    notifications: NotificationEntity[]
  ): NotificationEntity[] {
    const now = new Date();
    const todayUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );
    const tomorrowUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    );

    return notifications.filter((notification) => {
      const notificationDate = new Date(notification.createdTime);
      const notificationUTC = new Date(
        Date.UTC(
          notificationDate.getUTCFullYear(),
          notificationDate.getUTCMonth(),
          notificationDate.getUTCDate()
        )
      );

      // Check if notification is today (between today 00:00 UTC and tomorrow 00:00 UTC)
      return (
        notificationUTC.getTime() >= todayUTC.getTime() &&
        notificationUTC.getTime() < tomorrowUTC.getTime()
      );
    });
  }

  /**
   * Get previous notifications (before today) from a list
   * Compares dates in UTC to avoid timezone issues
   */
  getPreviousNotifications(
    notifications: NotificationEntity[]
  ): NotificationEntity[] {
    const now = new Date();
    const todayUTC = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
    );

    return notifications.filter((notification) => {
      const notificationDate = new Date(notification.createdTime);
      const notificationUTC = new Date(
        Date.UTC(
          notificationDate.getUTCFullYear(),
          notificationDate.getUTCMonth(),
          notificationDate.getUTCDate()
        )
      );

      // Check if notification is before today
      return notificationUTC.getTime() < todayUTC.getTime();
    });
  }

  /**
   * Switch between read and unread tabs
   */
  setActiveTab(tab: 'unread' | 'read'): void {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  /**
   * Check if notification is a reminder that can be acted upon
   * Only actionable if appointment status is 'pending'
   */
  isActionableReminder(notification: NotificationEntity): boolean {
    if (notification.type !== 'REMINDER') return false;
    if (!notification.data?.appointmentId) return false;

    const appointmentId = notification.data.appointmentId;

    // Check cached status first (from previous fetches)
    const cachedStatus = this.appointmentStatusCache.get(appointmentId);
    if (cachedStatus && cachedStatus !== 'pending') {
      return false;
    }
    if (cachedStatus === 'pending') {
      // Still need to check other conditions
    }

    // Check appointment status from notification data
    const notificationStatus = notification.data?.appointmentStatus;
    if (notificationStatus && notificationStatus !== 'pending') {
      // Cache the status
      this.appointmentStatusCache.set(appointmentId, notificationStatus);
      return false;
    }

    // Check if appointment exists in upcoming appointments
    const appointment = this.upcomingAppointments.find(
      (a) => a.id === appointmentId
    );
    
    if (appointment) {
      // Cache the status
      this.appointmentStatusCache.set(appointmentId, appointment.status);
      // Only actionable if status is 'pending'
      return appointment.status === 'pending';
    }

    // If appointment not found in store, it might be:
    // 1. Confirmed/canceled (not in "upcoming" list) - need to fetch it
    // 2. Store hasn't loaded yet
    // 3. Date has passed

    // Check if appointment date has passed
    if (this.isAppointmentPast(notification.data?.date)) {
      return false;
    }

    // If we have a cached status, use it
    if (cachedStatus === 'pending') {
      return true;
    }
    if (cachedStatus && cachedStatus !== 'pending') {
      return false;
    }
    
    // If appointment not in store and not cached, fetch it asynchronously
    if (notificationStatus === 'pending') {
      // Fetch appointment to verify status (async, won't block template)
      this.fetchAppointmentStatusIfNeeded(appointmentId);
      // Optimistically show as actionable if notification says pending
      // Will be updated when fetch completes
    return true;
    }
    
    // If notification status is undefined or missing, don't assume it's actionable
    // This prevents showing actions for confirmed appointments with missing status
    return false;
  }

  /**
   * Fetch appointment status if not already cached or in store
   */
  private fetchAppointmentStatusIfNeeded(appointmentId: string): void {
    // Don't fetch if already cached or in store
    if (this.appointmentStatusCache.has(appointmentId)) {
      return;
    }
    if (this.upcomingAppointments.some(a => a.id === appointmentId)) {
      return;
    }

    // Fetch appointment to get current status
    this.appointmentService.getAppointmentById(appointmentId).subscribe({
      next: (appointment) => {
        this.appointmentStatusCache.set(appointmentId, appointment.status);
        // Trigger change detection to update the view
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error fetching appointment status:', error);
        // On error, cache as 'unknown' to prevent repeated fetches
        this.appointmentStatusCache.set(appointmentId, 'unknown');
      }
    });
  }

  /**
   * Check if appointment date has passed
   */
  private isAppointmentPast(appointmentDate: string): boolean {
    if (!appointmentDate) return true;

    try {
      const apptDate = new Date(appointmentDate);
      const now = new Date();
      return apptDate.getTime() < now.getTime();
    } catch (error) {
      console.error('Error parsing appointment date:', error);
      return true;
    }
  }

  /**
   * Handle confirm appointment action
   */
  async confirmAppointment(
    notification: NotificationEntity,
    event: Event
  ): Promise<void> {
    event.stopPropagation(); // Prevent notification click

    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const appointmentId = notification.data?.appointmentId;
      if (!appointmentId) {
        this.toastService.error('Error', 'Appointment ID not found.');
        return;
      }

      console.log('✅ Confirming appointment:', appointmentId);

      // Call backend API to confirm appointment
      await this.appointmentService
        .confirmAppointment(appointmentId)
        .toPromise();

      // Mark notification as read
      await this.markAsRead(notification.id);

      // Show success message
      this.toastService.success(
        'Success',
        'Appointment confirmed successfully!'
      );

      // Refresh appointments in store
      this.store.dispatch(AppointmentActions.refreshUpcomingAppointments());

      // Refresh notifications
      await this.refreshNotifications();
    } catch (error) {
      console.error('❌ Error confirming appointment:', error);
      this.toastService.error(
        'Error',
        'Failed to confirm appointment. Please try again.'
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Handle cancel appointment action
   */
  async cancelAppointment(
    notification: NotificationEntity,
    event: Event
  ): Promise<void> {
    event.stopPropagation(); // Prevent notification click

    // Show confirmation dialog
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = 'Cancel Appointment';
    modalRef.componentInstance.message = 'Are you sure you want to cancel this appointment? This action cannot be undone.';
    modalRef.componentInstance.confirmText = 'Yes, Cancel';
    modalRef.componentInstance.cancelText = 'No, Keep Appointment';
    modalRef.componentInstance.confirmButtonClass = 'btn-danger';
    modalRef.componentInstance.cancelButtonClass = 'btn-secondary';
    modalRef.componentInstance.icon = 'fas fa-exclamation-triangle';
    modalRef.componentInstance.iconColor = 'text-danger';

    try {
      const result = await modalRef.result;
      
      // User clicked cancel or closed the modal
      if (result !== 'confirm') {
        return;
      }
    } catch (error) {
      // User dismissed the modal (clicked outside or pressed ESC)
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const appointmentId = notification.data?.appointmentId;
      if (!appointmentId) {
        this.toastService.error('Error', 'Appointment ID not found.');
        return;
      }

      console.log('❌ Cancelling appointment:', appointmentId);

      // Call backend API to cancel appointment
      await this.appointmentService
        .cancelAppointment(appointmentId)
        .toPromise();

      // Mark notification as read
      await this.markAsRead(notification.id);

      // Show success message
      this.toastService.success(
        'Success',
        'Appointment cancelled successfully'
      );

      // Refresh appointments in store
      this.store.dispatch(AppointmentActions.refreshUpcomingAppointments());

      // Refresh notifications
      await this.refreshNotifications();
    } catch (error) {
      console.error('❌ Error cancelling appointment:', error);
      this.toastService.error(
        'Error',
        'Failed to cancel appointment. Please try again.'
      );
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
    this.cdr.markForCheck();

    // Close dropdown when clicking outside (handled by navbar component)
    if (this.showDropdown) {
      // Add click listener to document to close dropdown when clicking outside
      setTimeout(() => {
        document.addEventListener('click', this.closeDropdownOnOutsideClick);
      }, 0);
    } else {
      document.removeEventListener('click', this.closeDropdownOnOutsideClick);
    }
  }

  /**
   * Close dropdown when clicking outside
   */
  private closeDropdownOnOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const notificationContainer = document.querySelector('.notification-btn');
    
    if (notificationContainer && !notificationContainer.contains(target)) {
      this.showDropdown = false;
      this.cdr.markForCheck();
      document.removeEventListener('click', this.closeDropdownOnOutsideClick);
    }
  };

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.showDropdown = false;
    document.removeEventListener('click', this.closeDropdownOnOutsideClick);
    this.cdr.markForCheck();
  }
}
